import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AdminContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

export function AdminProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);

  const authFetch = useCallback(async (path, opts = {}) => {
    const t = token || localStorage.getItem('admin_token');
    const headers = { ...opts.headers };
    if (t) headers['Authorization'] = `Bearer ${t}`;
    if (!(opts.body instanceof FormData)) headers['Content-Type'] = 'application/json';
    const res = await fetch(`${API}${path}`, { ...opts, headers });
    if (res.status === 401) { logout(); throw new Error('Unauthorized'); }
    if (res.status === 403) throw new Error('Forbidden');
    return res;
  }, [token]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Login failed'); }
    const data = await res.json();
    localStorage.setItem('admin_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/api/admin/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(u => setUser(u))
      .catch(() => { localStorage.removeItem('admin_token'); setToken(null); })
      .finally(() => setLoading(false));
  }, [token]);

  const hasRole = (...roles) => user && roles.includes(user.role);
  const isAdmin = () => hasRole('admin');
  const canWrite = () => hasRole('admin', 'operations');
  const isAccounting = () => hasRole('accounting');

  return (
    <AdminContext.Provider value={{ user, token, loading, login, logout, authFetch, hasRole, isAdmin, canWrite, isAccounting }}>
      {children}
    </AdminContext.Provider>
  );
}

export const useAdmin = () => useContext(AdminContext);
